import React, { useState } from "react";
import { Link } from "react-router-dom";
import MarketingNav from "@/components/site/MarketingNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("Check your inbox");
    } catch (err) {
      // We still show the success view for enumeration protection, but toast a hint
      setSent(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-background" data-testid="forgot-password-page">
      <MarketingNav />
      <div className="max-w-md mx-auto px-6 md:px-8 py-16 md:py-24">
        <Link to="/login" className="inline-flex items-center gap-2 overline text-muted-foreground hover:text-primary" data-testid="back-to-login-link">
          <ArrowLeft className="w-3 h-3" /> Back to sign in
        </Link>
        <div className="overline text-muted-foreground mt-8">Password recovery</div>
        <h1 className="font-serif-display text-5xl md:text-6xl leading-none tracking-tight mt-3">
          Forgot it?<br /><span className="italic text-primary">We'll fix it.</span>
        </h1>

        {!sent ? (
          <>
            <p className="mt-6 text-sm text-muted-foreground max-w-md">
              Enter the email tied to your Draftwave account and we'll send a one-time reset
              link. The link expires in 60 minutes.
            </p>
            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="forgot-form">
              <div>
                <label className="overline text-muted-foreground">Email</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@company.com"
                  className="rounded-none mt-2 h-11 font-mono text-sm"
                  data-testid="forgot-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={busy || !email}
                className="rounded-none h-12 px-8 font-mono uppercase tracking-widest text-xs w-full md:w-auto"
                data-testid="forgot-submit-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
              </Button>
            </form>
          </>
        ) : (
          <div className="mt-8 border border-border p-6 bg-background/60 backdrop-blur-sm" data-testid="forgot-sent">
            <div className="overline text-primary text-[10px]">Check your inbox</div>
            <h2 className="font-serif-display text-2xl mt-2 leading-tight">
              If <span className="italic">{email}</span> exists, a reset link is on its way.
            </h2>
            <p className="mt-3 text-sm text-muted-foreground">
              The link expires in 60 minutes. If you don't see the email in 2–3 minutes, check
              your spam folder or try again with a different address.
            </p>
            <Link to="/login" className="mt-6 inline-block overline text-primary hover-invert">
              ← Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
